// to easily copy and paste components
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import {useTranslation} from 'react-i18next';
import Loader from "../../common/simple/loader";
import {BllinkLogger} from "../../common/utils/bllink_loggers";
import {Modal} from 'react-bootstrap';
import {Formik} from 'formik';
import {StyledModal} from '../../common/shared/modal.styled';
import {useDispatch} from 'react-redux';
import {showLoader, hideLoader} from '../../store/common/slice';
import {addVendor} from '../../store/expenses/actions';
import { constants } from '../../common/constants/constants';

export default function NewSupplierPopUp({show, onModalHide, onVendorAdded, building, buildingID}) {
    const [bankOptions, setBankOptions] = useState([]);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const validationSchema = yup.object().shape({
        name: yup.string().required(t('titles.forms.mandatory_field')),
        email: yup.string().email(t('titles.forms.correct_email')),
        phoneNumber: yup.string().matches(/^[0-9]+$/, t('titles.forms.correct_phone_for_vendor')).min(3).max(10),
        comment: yup.string().max(100),
        vendorTaxID: yup.string().min(6, t('titles.forms.vendor_tax_id')),
        bank: yup.number().oneOf(bankOptions.map(i => +i.value)).when(['branch', 'account'], {
            is: (a, b) => a !== undefined || b !== undefined,
            then: yup.number().required(t('titles.forms.correct_account_bank_branch'))
        }),
        branch: yup.string().matches(/^\d+$/, t('titles.forms.number_field')).min(1).max(3).when(['bank', 'account'], {
            is: (a, b) => a !== undefined || b !== undefined,
            then: yup.string().required(t('titles.forms.correct_account_bank_branch'))
        }),
        account: yup.string().matches(/^\d+$/, t('titles.forms.number_field')).min(6).max(9).when(['branch', 'bank'], {
            is: (a, b) => a !== undefined || b !== undefined,
            then: yup.string().required(t('titles.forms.correct_account_bank_branch'))
        }),
    }, [['branch', 'account'], ['bank', 'account'], ['branch', 'bank']]);
    const initialFormValues = {
        name: '',
        email: '',
        phoneNumber: '',
        vendorTaxID: '',
        comment: '',
        bank: '',
        branch: '',
        account: '',
    };

    const submitForm = async (evt, values) => {
        evt.preventDefault();
        dispatch(showLoader());
        const dataPayload = {
            name: values.name,
            firstName: values.name,
            lastName: values.name,
            ownerBuildingID: buildingID,
            ...(values.comment && {comment: values.comment}),
            ...(values.phoneNumber && {phone: values.phoneNumber}),
            ...(values.vendorTaxID && {taxID: values.vendorTaxID}),
            ...(values.bank && {bank: values.bank}),
            ...(values.branch && {branch: values.branch}),
            ...(values.account && {account: values.account}),
        }
        BllinkLogger.info(`payload for submitting`, dataPayload);

        const {payload} = await dispatch(addVendor(dataPayload));
        dispatch(hideLoader());

        if (payload && payload.externalId) {
            alert(t('expenses.vendor_created'));
            onVendorAdded();
        }
    }

    useEffect(() => {
        // temp solution here. need to figure out why building is empty
        let currency = building.currency;
        if (!building.id) {
            currency= constants.defaultCurrency;
        }
        const codes = currency.toUpperCase() === 'MXN' ? constants.bankCodesInMexico : constants.bankCodesInIsrael;
        const country = currency.toUpperCase() === 'MXN' ? 'mexico' : 'israel';
        setBankOptions(codes.map(value => ({ value: +value, label: `${value} - ${t(`common.banks.${country}.${value}`)}` })));

    }, [building.id]);

    return (
        <StyledModal
            dialogClassName={'supplier-modal'}
            show={show}
            onHide={onModalHide}
            centered
        >
            <Modal.Header>
                <Modal.Title className={'text-center'}>
                    {t('expenses.new_supplier')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Loader/>
                <Formik
                    initialValues={initialFormValues}
                    validationSchema={validationSchema}
                >
                    {({
                          errors,
                          touched,
                          values,
                          handleChange,
                          handleBlur,
                          isValid,
                          dirty
                    }) => (
                        <form id="js-new-vendor" name="js-new-vendor">
                            <div className="row">
                                <div className="col-sm-4">
                                    <div className="form-group">
                                        <label htmlFor="name">{t('expenses.name')} </label>
                                        <input
                                            type="text"
                                            className={`form-control bllink-input-border ${errors.name && touched.name ? 'error' : ''}`}
                                            name="name"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.name}
                                        />
                                        {
                                            errors.name && touched.name && (
                                                <label className={'error'}>{errors.name}</label>
                                            )
                                        }
                                    </div>
                                </div>
                                <div className="col-sm-4">
                                    <div className="form-group">
                                        <label htmlFor="email">{t('expenses.email')} </label>
                                        <input
                                            type="text"
                                            className={`form-control bllink-input-border ${errors.email && touched.email ? 'error' : ''}`}
                                            name="email"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.email}
                                        />
                                        {
                                            errors.email && touched.email && (
                                                <label className={'error'}>{errors.email}</label>
                                            )
                                        }
                                    </div>
                                </div>

                                <div className="col-sm-4">
                                    <div className="form-group">
                                        <label htmlFor="phoneNumber">{t('expenses.phone_number')} </label>
                                        <input
                                            type="text"
                                            className={`form-control bllink-input-border ${errors.phoneNumber && touched.phoneNumber ? 'error' : ''}`}
                                            name="phoneNumber"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.phoneNumber}
                                        />
                                        {
                                            errors.phoneNumber && touched.phoneNumber && (
                                                <label className={'error'}>{errors.phoneNumber}</label>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-8">
                                    <div className="form-group">
                                        <label htmlFor="comment">{t('expenses.comment')} </label>
                                        <input
                                            type="text"
                                            className={`form-control bllink-input-border ${errors.comment && touched.comment ? 'error' : ''}`}
                                            name="comment"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.comment}
                                        />
                                        {
                                            errors.comment && touched.comment && (
                                                <label className={'error'}>{errors.comment}</label>
                                            )
                                        }
                                    </div>
                                </div>
                                <div className="col-sm-4">
                                    <div className="form-group">
                                        <label htmlFor="vendorTaxID">{t('expenses.vat_number')} </label>
                                        <input
                                            type="text"
                                            className="form-control bllink-input-border"
                                            name="vendorTaxID"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.vendorTaxID}
                                        />
                                        {
                                            errors.vendorTaxID && touched.vendorTaxID && (
                                                <label className={'error'}>{errors.vendorTaxID}</label>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-4">
                                    <div className="form-group">
                                        <label htmlFor="bank">{t('expenses.bank')} </label>
                                        <select
                                            className={`form-control bllink-input-border ${errors.bank && touched.bank ? 'error' : ''}`}
                                            name="bank"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.bank}
                                        >
                                            <option value="" className='hidden-empty-option'></option>
                                            {bankOptions.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                                        </select>

                                        {
                                            errors.bank && touched.bank && (
                                                <label className={'error'}>{errors.bank}</label>
                                            )
                                        }
                                    </div>
                                </div>
                                <div className="col-sm-4">
                                    <div className="form-group">
                                        <label htmlFor="branch">{t('expenses.branch')} </label>
                                        <input
                                            type="text"
                                            className={`form-control bllink-input-border ${errors.branch && touched.branch ? 'error' : ''}`}
                                            name="branch"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.branch}
                                        />
                                        {
                                            errors.branch && touched.branch && (
                                                <label className={'error'}>{errors.branch}</label>
                                            )
                                        }
                                    </div>
                                </div>
                                <div className="col-sm-4">
                                    <div className="form-group">
                                        <label htmlFor="account">{t('expenses.account')} </label>
                                        <input
                                            type="text"
                                            className={`form-control bllink-input-border ${errors.account && touched.account ? 'error' : ''}`}
                                            name="account"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.account}
                                        />
                                        {
                                            errors.account && touched.account && (
                                                <label className={'error'}>{errors.account}</label>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-sm-9"/>
                                <div className="col-sm-3">
                                    <button type="submit"
                                            className="bllink-button"
                                            disabled={!(isValid && dirty)}
                                            onClick={(evt) => submitForm(evt, values)}
                                    >{t('expenses.add_vendor')}</button>
                                </div>
                            </div>
                        </form>
                    )}
                </Formik>
            </Modal.Body>
        </StyledModal>
    );
}


